import type { RouteRecordName } from 'vue-router'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals/attribution'

import { trackWebPerformanceAll as sendAmplitudeWebPerformanceEvent } from './amplitude/events'
import {
  trackWebPerformance as sendNoodleWebPerformanceEvent,
  trackRumView,
} from './noodle/events'
import type { CloudflareData, PageViewData } from './types'
import { getCacheStatus } from './utils/getCacheStatus'
import { getSelector } from './utils/getSelector'
import { getVerifiedBot } from './utils/getVerifiedBot'
import {
  logCLSAttributionToDatadog,
  logINPAttributionToDatadog,
} from './utils/logWebVitalAttribution'

function getConnection() {
  if (!process.client) {
    return 'unknown'
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/effectiveType
  return window.navigator?.connection?.effectiveType || 'unknown'
}

function getDeviceType() {
  // only works on client side, so we are mobile first on server side
  if (!process.client) {
    return 'mobile'
  }

  // TODO [FRONT-827] This need to be shared between modules.
  const breakpoints = [
    '(min-width: 992px)',
    '(min-width: 768px)',
    '(min-width: 480px)',
    '(max-width: 480px)',
  ]

  const matchMedia = (media: string) => window.matchMedia(media).matches
  const breakpoint = breakpoints.length - breakpoints.findIndex(matchMedia) - 1

  return breakpoint < 2 ? 'mobile' : 'desktop'
}

function startMonitoringWebVitals(
  pageData: PageViewData,
  cloudflareData: CloudflareData,
  options: {
    logINPAttribution: boolean | undefined
    logCLSAttribution: boolean | undefined
  },
) {
  const noodleData = {
    ...pageData,
    ...cloudflareData,
  }
  onCLS((metric) => {
    sendNoodleWebPerformanceEvent({
      ...noodleData,
      metric: 'cls',
      value: metric.value,
    })

    if (options.logCLSAttribution) logCLSAttributionToDatadog(metric, pageData)
  })

  onFCP(({ value }) =>
    sendNoodleWebPerformanceEvent({
      ...noodleData,
      metric: 'fcp',
      value,
    }),
  )

  onLCP(({ value }) =>
    sendNoodleWebPerformanceEvent({
      ...noodleData,
      metric: 'lcp',
      value,
    }),
  )

  onTTFB(({ value }) =>
    sendNoodleWebPerformanceEvent({
      ...noodleData,
      metric: 'ttfb',
      value,
    }),
  )

  onINP(
    (metric) => {
      sendNoodleWebPerformanceEvent({
        ...noodleData,
        metric: 'inp',
        value: metric.value,
      })

      sendAmplitudeWebPerformanceEvent({
        ...pageData,
        metric: 'inp',
        value: metric.value,
      })

      if (options.logINPAttribution) {
        logINPAttributionToDatadog(metric, pageData)
      }
    },
    {
      generateTarget: (el) => {
        if (el) {
          const targetElement =
            el instanceof Element && typeof el.closest === 'function'
              ? el.closest('button, a') || el
              : el
          const targetElementSelector = getSelector(targetElement)

          try {
            const location = new URL(el.baseURI).pathname

            return `${location}|${targetElementSelector}`
          } catch (e) {
            return `unknown|${targetElementSelector}`
          }
        }

        return 'unknown|unknown'
      },
    },
  )
}

export function initWebVitals({
  routeName,
  country,
  webVitalsMonitoringEnabled,
  logINPAttribution,
  logCLSAttribution,
}: {
  routeName: RouteRecordName
  country: MarketCountryCode
  webVitalsMonitoringEnabled: boolean
  logINPAttribution?: boolean
  logCLSAttribution?: boolean
}) {
  const connection = getConnection()
  const deviceType = getDeviceType()

  const data = {
    pageType: String(routeName),
    locale: useI18nLocale(),
    connection,
    deviceType,
    country,
  }
  const cloudflareData = {
    cachestatus: getCacheStatus(),
    verifiedbot: getVerifiedBot(),
  }

  if (webVitalsMonitoringEnabled) {
    startMonitoringWebVitals(data, cloudflareData, {
      logINPAttribution,
      logCLSAttribution,
    })
  }
  trackRumView(data)
}
